import { BoxGeometry, Group, Mesh, PlaneGeometry, type MeshStandardMaterial } from 'three';
import { flatMaterial } from '../../render/materials/cache';
import { mergeByMaterial } from '../../render/mergeByMaterial';
import { LAYOUT } from './roadLayout';

export interface EdgeMaterials {
  kerb: MeshStandardMaterial;
  median: MeshStandardMaterial;
}

export interface Edges {
  group: Group;
  barrierMedian: Group;
  grassMedian: Group;
}

/** A tile-local span along the street, as positive metres from the tile start. */
export interface Span {
  from: number;
  to: number;
}

function strip(width: number, span: Span, material: MeshStandardMaterial, x: number, y: number): Mesh {
  const length = span.to - span.from;
  const mesh = new Mesh(new PlaneGeometry(width, length), material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(x, y, -(span.from + length / 2));
  mesh.receiveShadow = true;
  return mesh;
}

function bar(width: number, height: number, span: Span, material: MeshStandardMaterial, x: number): Mesh {
  const length = span.to - span.from;
  const mesh = new Mesh(new BoxGeometry(width, height, length), material);
  mesh.position.set(x, height / 2, -(span.from + length / 2));
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Kerbs, gutter, pavements and both median styles over the given spans (one span, or two either side of a junction).
 * `medianSpans` lets the median stop short of the rest (the gap where trotros U-turn at a terminus).
 */
export function buildEdges(spans: readonly Span[], m: EdgeMaterials, medianSpans: readonly Span[] = spans): Edges {
  const group = new Group();
  const barrierMedian = new Group();
  const grassMedian = new Group();
  const w = LAYOUT.medianWidth;
  for (const span of spans) {
    group.add(
      strip(LAYOUT.gutterWidth, span, flatMaterial('#2a2622'), LAYOUT.gutterX, -0.25),
      bar(0.3, 0.25, span, m.kerb, LAYOUT.kerbX),
      bar(LAYOUT.sidewalkWidth, 0.2, span, flatMaterial('#b0a591'), LAYOUT.sidewalkX),
      bar(LAYOUT.sidewalkWidth, 0.2, span, flatMaterial('#b0a591'), LAYOUT.leftSidewalkX),
    );
  }
  for (const span of medianSpans) {
    barrierMedian.add(bar(w, 0.12, span, flatMaterial('#5a5a5a'), LAYOUT.medianX), bar(0.5, 0.8, span, m.median, LAYOUT.medianX));
    grassMedian.add(
      bar(w, 0.25, span, flatMaterial('#5b7440'), LAYOUT.medianX),
      bar(0.2, 0.3, span, m.kerb, LAYOUT.medianX + w / 2),
      bar(0.2, 0.3, span, m.kerb, LAYOUT.medianX - w / 2),
    );
  }
  // Merge by material so a tile's kerbs and pavements cost a handful of draw calls, not dozens.
  const merged = mergeByMaterial(group, { receiveShadow: true });
  const barrier = mergeByMaterial(barrierMedian, { receiveShadow: true });
  const grass = mergeByMaterial(grassMedian, { receiveShadow: true });
  merged.add(barrier, grass);
  return { group: merged, barrierMedian: barrier, grassMedian: grass };
}
